import api from '@/shared/api';
import type { Position, PositionResponse, SinglePositionResponse } from '../model/types';
import type { AxiosResponse } from 'axios';
import {
  buildTruthySearchParams,
  handleApiError,
  requestExcelBlobExport,
  type BulkDeleteResult,
  withQuery,
} from '@/shared/lib';

export interface ExportPositionsExcelParams {
  search?: string;
  sort?: string;
  order?: string;
  active?: boolean;
  branchId?: number;
  departmentId?: number;
}

const POSITIONS_EXPORT_QUERY_KEYS = [
  'search',
  'sort',
  'order',
  'active',
  'branchId',
  'departmentId',
] as const;

interface PaginationMetadata {
  total: number;
  page: number;
  itemsPerPage: number;
  totalPages: number;
}

interface PaginatedPositionResponse {
  success: boolean;
  data: Position[];
  metadata: PaginationMetadata;
}

export interface PositionByToken {
  positionId: number;
  positionName: string;
}

interface PositionsByTokenResponse {
  success: boolean;
  data: PositionByToken[];
  message?: string;
}

interface ClonePermissionsResponse {
  success: boolean;
  data: { targetId: number; sourceId: number; permissionsCount: number };
  message?: string;
}

class PositionApi {
  async getAllPositions(params?: {
    page?: number;
    itemsPerPage?: number;
    sort?: string;
    order?: string;
    branchId?: number;
  }): Promise<PositionResponse> {
    try {
      const response: AxiosResponse<PositionResponse> = await api.get(
        withQuery('/positions', params)
      );
      return response.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar cargos');
    }
  }

  async getPositionsByToken(): Promise<PositionByToken[]> {
    try {
      const response: AxiosResponse<PositionsByTokenResponse> = await api.get('/positions/token');
      return response.data.data ?? [];
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar os cargos do usuário');
    }
  }

  async getPositionById(positionId: number | string): Promise<Position> {
    try {
      const response: AxiosResponse<SinglePositionResponse> = await api.get(
        `/positions/${positionId}`
      );
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao buscar cargo');
    }
  }

  async deletePosition(positionId: number | string): Promise<void> {
    try {
      await api.delete(`/positions/${positionId}`);
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar cargo');
    }
  }

  async createPosition(data: {
    name: string;
    departmentId?: number;
    active?: boolean;
  }): Promise<Position> {
    try {
      const response = await api.post('/positions', {
        name: data.name,
        departmentId: data.departmentId,
        active: data.active ?? true,
      });
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao criar cargo, tente novamente.');
    }
  }

  async updatePosition(
    positionId: number | string,
    data: Partial<Position> & { branchId?: number }
  ): Promise<any> {
    try {
      const dataWithoutBranch = { ...data };
      delete dataWithoutBranch.branchId;
      const response = await api.patch(`/positions/${positionId}`, dataWithoutBranch);
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao salvar edições, tente novamente.');
    }
  }

  async checkLinks(ids: number[]): Promise<{ hasLinks: boolean }> {
    try {
      const response = await api.post('/positions/links-check', { ids });
      return response.data.data;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao verificar vínculos dos cargos');
    }
  }

  async bulkDeletePositions(positionIds: number[]): Promise<AxiosResponse<any>> {
    try {
      const response = await api.delete('/positions/bulk', {
        data: { ids: positionIds },
      });
      return response;
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar cargos em lote');
    }
  }

  async bulkDelete(ids: number[]): Promise<{ data: BulkDeleteResult }> {
    try {
      const response = await api.delete('/positions/bulk', {
        data: { ids },
      });
      return { data: response.data.data };
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao deletar cargos em lote');
    }
  }

  async exportPositionsExcel(
    params: ExportPositionsExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, POSITIONS_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/positions/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'cargos.xlsx',
      'Tente novamente.'
    );
  }

  async searchPositions(params: {
    search?: string;
    page?: number;
    limit?: number;
    active?: boolean;
    type?: 'POSITION' | 'FUNCTIONAL_ACTIVITY';
    branchId?: number;
    departmentId?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<{ data: Position[]; metadata: PaginationMetadata }> {
    try {
      const response: AxiosResponse<PaginatedPositionResponse> = await api.get(
        withQuery('/positions', params)
      );

      return {
        data: response.data.data || [],
        metadata: response.data.metadata || {
          total: 0,
          page: 1,
          itemsPerPage: 10,
          totalPages: 0,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar cargos:', error);
      throw error;
    }
  }

  async clonePermissions(
    sourcePositionId: number,
    targetPositionId: number
  ): Promise<{ permissionsCount: number }> {
    try {
      const response: AxiosResponse<ClonePermissionsResponse> = await api.post(
        `/positions/${targetPositionId}/clone-permissions`,
        { sourceId: sourcePositionId }
      );

      return { permissionsCount: response.data.data.permissionsCount };
    } catch (error: unknown) {
      throw handleApiError(error, 'Erro ao clonar permissões do cargo');
    }
  }
}

export const positionApi = new PositionApi();
